// FEATURE-40 — sanitize the host's `apiBase` once at `init()` time so the
// panel's "join with /api/v1/..." path-builder never produces double prefixes
// like `/api/api/v1/chat/stream`. Host sites are free to pass any of these
// equivalent forms (full accepted list lives on `InitOptions.apiBase` JSDoc):
//
//   ''                          // same origin
//   'https://x'                 // bare origin
//   'https://x/'                // trailing slash
//   'https://x/api'             // origin + '/api'
//   'https://x/api/v1'          // origin + '/api/v1'
//   '/api', '/api/v1', '/'      // same-origin paths
//
// Canonical form: trailing slashes removed, trailing `/api` or `/api/v1`
// segment stripped. Empty string means "same origin" — the panel resolves
// URLs against its own origin.
//
// Out of scope: trimming leading `/api` segments that are NOT trailing
// (e.g. `/foo/api/v2/bar` is left as-is — that's the host's mount choice,
// not a normalization concern).

const TRAILING_API_V1 = /\/api\/v1$/i;
const TRAILING_API = /\/api$/i;

/**
 * Normalize the host-supplied `apiBase` to its canonical form. See the file
 * header for accepted forms and rules.
 *
 * @throws {Error} when the input is empty / whitespace only.
 */
export function normalizeApiBase(input: string): string {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    throw new Error('BAAssistant.init: apiBase is required');
  }
  // Strip trailing slashes first so a value like `'https://x/api/v1/'`
  // matches the `/api/v1$` tail.
  let value = trimmed.replace(/\/+$/, '');
  if (TRAILING_API_V1.test(value)) {
    value = value.replace(TRAILING_API_V1, '');
  } else if (TRAILING_API.test(value)) {
    value = value.replace(TRAILING_API, '');
  }
  // Step (5): strip any trailing slash left after the suffix removal. This
  // can happen when the input was `'/api/'` → after the trailing-slash strip
  // it's `'/api'` → after the suffix strip it's `''`, which is fine; but
  // `'/api/v1/api/'` → `'/api/v1/api'` → `'/api/v1'` would still have no
  // trailing slash. Defensive in case future rules introduce one.
  value = value.replace(/\/+$/, '');
  return value;
}
